import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/useAuth';
import { mediaUrl } from '../api';

function UserMenu() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  // The panel opens for an administrator of any single department.
  const hasAdminAccess = Boolean(
    user?.rightsAnywhere?.includes('admin.access'),
  );

  const canManageInterface = Boolean(
    user?.rights?.includes('interface.manage'),
  );

  const canManageMedia = Boolean(
    user?.rights?.includes('media.manage'),
  );

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function openMenu() {
    clearCloseTimer();
    setIsOpen(true);
  }

  function scheduleCloseMenu() {
    clearCloseTimer();

    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 180);
  }

  function toggleMenu() {
    clearCloseTimer();
    setIsOpen((currentValue) => !currentValue);
  }

  function closeMenu() {
    clearCloseTimer();
    setIsOpen(false);
  }

  async function handleLogout() {
    try {
      await logout();
      closeMenu();

      navigate('/login', {
        replace: true,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  useEffect(() => {
    function handleOutsideClick(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        clearCloseTimer();
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        clearCloseTimer();
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
      clearCloseTimer();
    };
  }, []);

  return (
    <div
      ref={menuRef}
      className={`user-menu-wrapper${isOpen ? ' open' : ''}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleCloseMenu}
    >
      <button
        className="user"
        type="button"
        aria-label={t('userMenu.openMenuAriaLabel')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggleMenu}
      >
        <i className="fa-solid fa-circle-user" />
      </button>


      <div
        className="user-menu"
        role="menu"
        aria-hidden={!isOpen}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleCloseMenu}
      >
        <div className="user-menu-profile">
          <div className="user-menu-avatar">
            {user?.avatarUrl ? (
              <img
                src={mediaUrl(user.avatarUrl)}
                alt={t('userMenu.userAvatarAlt', 'Аватар пользователя')}
                className="user-menu-avatar-image"
              />
            ) : (
              <i className="fa-solid fa-user" />
            )}
          </div>

          <div className="user-menu-identity">
            {user && (
              <>
                <span className="user-menu-name">
                  {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}
                </span>

                {user.username && <span className="user-menu-login">@{user.username}</span>}
              </>
            )}

            {(user?.departments?.length > 0 || user?.groups?.length > 0) && (
              <span className="user-menu-meta">
                {user.departments?.length > 0 && (
                  <span className="user-menu-meta-line" title={t('userMenu.departments')}>
                    <i className="fa-solid fa-sitemap" aria-hidden="true" />
                    <span className="visually-hidden">{t('userMenu.departments')}: </span>
                    {user.departments.map((department) => department.name).join(', ')}
                  </span>
                )}

                {user.groups?.length > 0 && (
                  <span className="user-menu-meta-line" title={t('userMenu.groups')}>
                    <i className="fa-solid fa-user-group" aria-hidden="true" />
                    <span className="visually-hidden">{t('userMenu.groups')}: </span>
                    {user.groups.slice(0, 2).map((group) => group.groupName).join(' · ')}

                    {user.groups.length > 2 && (
                      <span
                        className="user-menu-groups-more"
                        tabIndex={0}
                        aria-label={user.groups.slice(2).map((group) => group.groupName).join(', ')}
                      >
                        +{user.groups.length - 2}
                        <span className="user-menu-groups-tooltip">
                          {user.groups.slice(2).map((group) => group.groupName).join(', ')}
                        </span>
                      </span>
                    )}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        <div className="user-menu-divider" />
        <nav className="user-menu-navigation">
          <Link
            to="/account"
            className="user-menu-item"
            role="menuitem"
            onClick={closeMenu}
          >
            <span className="user-menu-item-icon">
              <i className="fa-solid fa-user-gear" />
            </span>

            <span className="user-menu-item-content">
              <strong>{t('userMenu.profileSettingsTitle')}</strong>
              <small>{t('userMenu.profileSettingsDescription')}</small>
            </span>

            <i className="fa-solid fa-chevron-right user-menu-chevron" />
          </Link>

          <div className="user-menu-divider" />

          {canManageInterface && (
            <Link
              to="/interface"
              className="user-menu-item"
              role="menuitem"
              onClick={closeMenu}
            >
              <span className="user-menu-item-icon">
                <i className="fa-solid fa-palette" />
              </span>

              <span className="user-menu-item-content">
                <strong>{t('userMenu.interfaceSettingsTitle')}</strong>
                <small>{t('userMenu.interfaceSettingsDescription')}</small>
              </span>

              <i className="fa-solid fa-chevron-right user-menu-chevron" />
            </Link>
          )}

          {canManageMedia && (
            <Link
              to="/admin/media"
              className="user-menu-item"
              role="menuitem"
              onClick={closeMenu}
            >
              <span className="user-menu-item-icon">
                <i className="fa-solid fa-photo-film" />
              </span>

              <span className="user-menu-item-content">
                <strong>{t('userMenu.mediaPoolTitle')}</strong>
                <small>{t('userMenu.mediaPoolDescription')}</small>
              </span>

              <i className="fa-solid fa-chevron-right user-menu-chevron" />
            </Link>
          )}
        </nav>

        {hasAdminAccess && (
          <>
            <div className="user-menu-divider" />

            <Link
              to="/admin"
              className="user-menu-item"
              role="menuitem"
              onClick={closeMenu}
            >
              <span className="user-menu-item-icon">
                <i className="fa-solid fa-user-shield" />
              </span>

              <span className="user-menu-item-content">
                <strong>{t('userMenu.adminPanelTitle')}</strong>
                <small>{t('userMenu.adminPanelDescription')}</small>
              </span>

              <i className="fa-solid fa-chevron-right user-menu-chevron" />
            </Link>
          </>
        )}

        <div className="user-menu-divider" />

        <button
          className="user-menu-item user-menu-logout"
          type="button"
          role="menuitem"
          onClick={handleLogout}
        >
          <span className="user-menu-item-icon">
            <i className="fa-solid fa-arrow-right-from-bracket" />
          </span>

          <span className="user-menu-item-content">
            <strong>{t('userMenu.logout')}</strong>
            <small>{t('userMenu.logoutDescription')}</small>
          </span>
        </button>
      </div>
    </div>
  );
}

export default UserMenu;
